/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { useState } from 'react'

import { ListControls } from '@/components/ui/list-controls/ListControls'
import { useDashboard } from '@/hooks'
import { exportToExcel } from '@/utils'
import { convertCelsius, tempUnitSuffix } from '@/utils/conversions'

import { TrackerColumnsSelector } from './TrackerColumnsSelector'
import { useTrackerMapContext } from './TrackerMapContext'

/** Limit to map, export and column chooser for the tracker list. */
export const TrackerListControls = () => {
  const context = useTrackerMapContext()
  const units = useDashboard().configuration.units as 'Imperial' | 'Metric'
  const [showColumnSelector, setShowColumnSelector] = useState(false)

  const handleExport = () => {
    // Export temperatures in the user's preferred units, matching the UI.
    const rows = context.filteredTrackers.map((t) => ({
      ...t,
      temperatureCelsius:
        t.temperatureCelsius != null ? Number(convertCelsius(t.temperatureCelsius, units).toFixed(1)) : null,
    }))
    exportToExcel(
      {
        shipmentName: 'Shipment',
        shipmentState: 'Shipment Status',
        originName: 'Origin',
        destinationName: 'Destination',
        label: 'Tracker',
        temperatureCelsius: `Temp (${tempUnitSuffix(units)})`,
        humidityPercent: 'Humidity (%)',
        lightLux: 'Light (lux)',
        batteryPercent: 'Battery (%)',
        imei: 'IMEI',
      },
      rows as unknown as Record<string, unknown>[],
      'Cooltrax_Trackers'
    )
  }

  return (
    <>
      <ListControls
        limitToMap={
          context.sidebarState.layoutMode !== 'table'
            ? {
                isOn: context.sidebarState.limitToMap,
                onToggle: () => context.setSidebarState((prev) => ({ ...prev, limitToMap: !prev.limitToMap })),
              }
            : undefined
        }
        onDownload={handleExport}
        onConfigure={() => setShowColumnSelector(true)}
        disabled={!context.companyId}
      />
      {showColumnSelector && <TrackerColumnsSelector onClose={() => setShowColumnSelector(false)} />}
    </>
  )
}
